import { ChangeEvent, useEffect, useRef, useState } from 'react';

import { Box, Button, TextareaAutosize, Typography } from '@mui/material';

import { useQuery } from 'react-query';

import { useInitialAutoMessages } from '@hooks_iqfu/dashboard/useInitialAutoMessages';

import { NotificationText } from '@constants_iqfu';
import { useNotificationContext } from '@contexts_common/notificationContext';

type Props = {
  companyId: string;
};

const InitialAutoMessage: React.FC<Props> = ({ companyId }: Props) => {
  const initialAutoMessageLength = 200;
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isDisabled = !isEditing;
  const [initialAutoMessages, setInterviewArticles] = useState<string | null>(null);
  const [hasRegularClosingDayOverviewItem, setHasRegularClosingDayOverviewItem] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { getInitialAutoMessages, postInitialAutoMessage } = useInitialAutoMessages();
  const { setNotificationDisplay, setNotificationMessage, setNotificationStatus } = useNotificationContext();

  const insertPlaceholder = (placeholder: string) => {
    if (!isEditing) return;
    setInterviewArticles((prev) => {
      const text = prev ?? '';
      const textarea = textareaRef.current;
      if (!textarea) return text + placeholder;

      const { selectionStart: start, selectionEnd: end } = textarea;
      const next = text.slice(0, start) + placeholder + text.slice(end);

      requestAnimationFrame(() => {
        const pos = start + placeholder.length;
        textarea.setSelectionRange(pos, pos);
        textarea.focus();
      });

      return next;
    });
  };

  const { data, refetch } = useQuery(
    ['initialAutoMessages', companyId],
    () => getInitialAutoMessages(Number(companyId)),
    { staleTime: 600 },
  );

  useEffect(() => {
    if (data && !isEditing) {
      setInterviewArticles(data.approved_content);
      setHasRegularClosingDayOverviewItem(data.has_regular_closing_day_overview_item);
    }
  }, [data]);

  const handleSubmit = async () => {
    if (initialAutoMessages === null) return;
    const requestVal = initialAutoMessages
      .replace(/\{+\s*seller_name\s*\}+/g, '{{seller_name}}')
      .replace(/\{+\s*regular_closing_day\s*\}+/g, '{{regular_closing_day}}');
    const result = await postInitialAutoMessage(Number(companyId), requestVal);

    if (!result) {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.initialAutoMessageCreateFailed);
    } else {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.initialAutoMessageCreated);
    }

    setNotificationDisplay(true);
    setIsEditing(false);
    if (result) refetch();
  };

  return (
    <Box sx={{ gap: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          paddingBottom: '16px',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Typography sx={{ padding: '8px 0', fontSize: '2rem', fontWeight: 'bold' }}>初回自動メッセージ</Typography>
        <Button
          sx={{
            width: '120px',
            padding: '8px 0',
            fontSize: '1.6rem',
            background: isEditing ? '#ccc' : '#207bb8',
            color: '#fff',
            '&:hover': {
              background: '#0b69a8',
            },
          }}
          disabled={isEditing}
          onClick={() => {
            setIsEditing(!isEditing);
          }}
        >
          更新
        </Button>
      </Box>
      <TextareaAutosize
        ref={textareaRef}
        value={initialAutoMessages || ''}
        minRows={10}
        disabled={isDisabled}
        style={{
          marginTop: '16px',
          width: '100%',
        }}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
          setInterviewArticles(event.target.value);
        }}
      />
      <Typography sx={{ width: '100%', textAlign: 'right', fontSize: '1.2rem', color: '#777' }}>
        {initialAutoMessages !== null ? initialAutoMessages.length : 0}/{initialAutoMessageLength}
      </Typography>
      <Box sx={{ gap: 2, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          sx={{ border: '1px solid #ccc', fontSize: '1.4rem', color: '#777' }}
          disabled={isDisabled}
          onClick={() => insertPlaceholder('{{seller_name}}')}
        >
          売主名様（フルネーム）
        </Button>
        {hasRegularClosingDayOverviewItem && (
          <Button
            sx={{ border: '1px solid #ccc', fontSize: '1.4rem', color: '#777' }}
            disabled={isDisabled}
            onClick={() => insertPlaceholder('{{regular_closing_day}}')}
          >
            定休日情報
          </Button>
        )}
      </Box>
      <Typography sx={{ fontSize: '1.4rem', color: '#777', mt: 1 }}>
        ※売主様名（フルネーム）には敬称は含まれません
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '16px', gap: 2 }}>
        <Button
          sx={{
            width: '120px',
            padding: '8px 0',
            fontSize: '1.6rem',
            background: isEditing ? '#fff' : '#ccc',
            border: '1px solid #ccc;',
            color: '#666',
            '&:hover': {
              background: 'darken(#fff, 6%)',
            },
          }}
          disabled={isDisabled}
          onClick={() => {
            setIsEditing(false);
            if (data?.approved_content) setInterviewArticles(data?.approved_content);
          }}
        >
          キャンセル
        </Button>
        <Button
          sx={{
            width: '120px',
            padding: '8px 0',
            fontSize: '1.6rem',
            background: isEditing ? '#207bb8' : '#ccc',
            color: '#fff',
            '&:hover': {
              background: '#0b69a8',
            },
          }}
          disabled={isDisabled}
          onClick={handleSubmit}
        >
          申請
        </Button>
      </Box>
    </Box>
  );
};

export default InitialAutoMessage;
